import type React from "react";
import {
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Chip,
  Skeleton,
  IconButton,
  TableContainer,
  Grid2,
} from "@mui/material";
import {
  Button,
  Popconfirm,
  Switch,
  Select,
  Input,
  Dropdown,
  MenuProps,
} from "antd";
import { useMemo, useState } from "react";
import { useExternalFont } from "contexts/externalFontContext";
import {
  FontAccessLevel,
  FontAccessLevelText,
  SearchType,
  SearchTypeText,
} from "types/enums";
import type { IAvailableExternalFontViewModel } from "viewModels";
import { formatExecutionTime } from "utils/timeHelpers";
import KpiCard from "components/KpiCard";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ChecklistIcon from "@mui/icons-material/Checklist";
import PlaylistRemoveIcon from "@mui/icons-material/PlaylistRemove";
import InsightsIcon from "@mui/icons-material/Insights";
import { CardContainer, CardHeader, GraphContainer } from "./styles";
import { Bar } from "@ant-design/plots";
import { MoreVert, Sort } from "@mui/icons-material";
import {
  SortAscendingOutlined,
  SortDescendingOutlined,
} from "@ant-design/icons";
import { IUpdateFontStatusInputModel } from "inputModels";
import FontTestModal from "components/Modal/FontTestModal";

import EditExternalFontModal from "components/Modal/EditExternalFontModal";
import { Edit, FlaskConical } from "lucide-react";

const FontManagerTab: React.FC = () => {
  const theme = useTheme();
  const {
    externalFonts,
    isLoadingFonts,
    isLoadingDetails,
    updateFontStatus,
    tests,
    clearFontMetrics,
  } = useExternalFont();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [usageLimit, setUsageLimit] = useState<number>(20);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedFont, setSelectedFont] =
    useState<IAvailableExternalFontViewModel | null>(null);
  const [metric, setMetric] = useState<"executions" | "time" | "successRate">(
    "time"
  );
  const [tableSort, setTableSort] = useState<{
    field: "name" | "type" | "successRate" | "executions" | "duration";
    order: "asc" | "desc";
  }>({ field: "name", order: "asc" });
  const [switchLoading, setSwitchLoading] = useState<Record<string, boolean>>(
    {}
  );
  const [clearMetricsLoading, setClearMetricsLoading] = useState(false);

  // Calcular estatísticas para os cards
  const stats = useMemo(() => {
    if (!externalFonts || externalFonts.length === 0) {
      return {
        totalFonts: 0,
        activeFonts: 0,
        inactiveFonts: 0,
        averageSuccessRate: 0,
      };
    }

    const activeFonts = externalFonts.filter((font) => !font.disabled).length;
    const fontsWithRate = externalFonts.filter(
      (font) => font.successRate != null
    );
    const averageSuccessRate =
      fontsWithRate.length > 0
        ? fontsWithRate.reduce(
            (sum, font) => sum + (font.successRate || 0),
            0
          ) / fontsWithRate.length
        : 0;

    return {
      totalFonts: externalFonts.length,
      activeFonts,
      inactiveFonts: externalFonts.length - activeFonts,
      averageSuccessRate,
    };
  }, [externalFonts]);

  // Dados combinados para o gráfico
  const chartData = useMemo(() => {
    if (!externalFonts || externalFonts.length === 0) return [];

    const filteredFonts = externalFonts
      .filter((font) => !font.disabled)
      .filter((font) => font.successRate != null || metric !== "successRate")
      .sort((a, b) => {
        const aValue =
          metric === "executions"
            ? a.executionCount ?? 0
            : metric === "time"
            ? a.averageExecutionTimeSeconds ?? 0
            : a.successRate ?? 0;
        const bValue =
          metric === "executions"
            ? b.executionCount ?? 0
            : metric === "time"
            ? b.averageExecutionTimeSeconds ?? 0
            : b.successRate ?? 0;
        return sortOrder === "desc" ? bValue - aValue : aValue - bValue;
      })
      .slice(0, usageLimit === -1 ? externalFonts.length : usageLimit)
      .map((font) => ({
        Nome: font.name,
        Valor:
          metric === "executions"
            ? font.executionCount ?? 0
            : metric === "time"
            ? font.averageExecutionTimeSeconds ?? 0
            : font.successRate ?? 0,
      }));
    return filteredFonts;
  }, [externalFonts, sortOrder, usageLimit, metric]);

  // Dados ordenados para a tabela
  const sortedFonts = useMemo(() => {
    if (!externalFonts || externalFonts.length === 0) return [];

    const compare = (
      a: IAvailableExternalFontViewModel,
      b: IAvailableExternalFontViewModel
    ) => {
      if (a.disabled !== b.disabled) {
        return a.disabled ? 1 : -1;
      }

      const { field, order } = tableSort;
      let comparison = 0;

      switch (field) {
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "type":
          comparison = SearchTypeText[a.type].localeCompare(
            SearchTypeText[b.type]
          );
          break;
        case "successRate":
          comparison = (a.successRate ?? -1) - (b.successRate ?? -1);
          break;
        case "executions":
          comparison = (a.executionCount ?? 0) - (b.executionCount ?? 0);
          break;
        case "duration":
          comparison =
            (a.averageExecutionTimeSeconds ?? 0) -
            (b.averageExecutionTimeSeconds ?? 0);
          break;
        default:
          comparison = a.name.localeCompare(b.name);
      }

      return order === "asc" ? comparison : -comparison;
    };

    const sorted = [...externalFonts].sort(compare);
    return sorted;
  }, [externalFonts, tableSort]);

  const getMenuItems = (
    font: IAvailableExternalFontViewModel
  ): MenuProps["items"] => {
    const isTestRunning = tests.some(
      (test) => test.fontKey === font.key && test.running
    );
    return [
      {
        key: "test",
        label: isTestRunning ? (
          <span>
            Testar Fonte{" "}
            <CircularProgress
              size={14}
              style={{ marginLeft: 8, verticalAlign: "middle" }}
            />
          </span>
        ) : (
          "Testar Fonte"
        ),
        icon: <FlaskConical size={18} />,
      },
      {
        key: "edit",
        label: "Editar Fonte",
        icon: <Edit size={18} />,
      },
    ];
  };

  const handleMenuAction = (
    action: string,
    font: IAvailableExternalFontViewModel
  ) => {
    setSelectedFont(font);
    if (action === "test") {
      setModalOpen(true);
    } else if (action === "edit") {
      setEditModalOpen(true);
    }
  };

  const barConfig = {
    data: chartData,
    xField: "Nome",
    yField: "Valor",
    barWidthRatio: 0.4,
    label: {
      text: "Valor",
      formatter: (value: number) => {
        return value > 0
          ? `${value} ${
              metric === "successRate" ? "%" : metric === "time" ? "s" : ""
            }`
          : "-";
      },
      style: {
        textAlign: (d: any) => (+d.Valor > 10 ? "right" : "start"),
        fill: (d: any) => (+d.Valor > 10 ? "#fff" : "#000"),
        dx: (d: any) => (+d.Valor > 10 ? -5 : 5),
      },
    },
    xAxis: {
      label: {
        rotate: -45,
        style: {
          textAlign: "center" as const,
          fill: "#000000",
          fontSize: 12,
        },
        formatter: (text: string) => {
          const maxLength = 30;
          return text.length > maxLength
            ? `${text.slice(0, maxLength - 3)}...`
            : text;
        },
      },
    },
    meta: {
      Nome: { alias: "Fonte" },
      Valor: {
        alias:
          metric === "executions"
            ? "Execuções"
            : metric === "time"
            ? "Tempo Médio (s)"
            : "Taxa de Sucesso (%)",
      },
    },
    responsive: true,
  };

  const getTypeBadgeColor = (type: SearchType) => {
    switch (type) {
      case SearchType.BOTH:
        return "#44aa49";
      case SearchType.CNPJ:
        return "#D81B60";
      case SearchType.CPF:
        return "#0288D1";
      default:
        return "#9E9E9E";
    }
  };

  const getAccessLevelBadgeColor = (type: FontAccessLevel) => {
    switch (type) {
      case FontAccessLevel.PUBLIC:
        return "#44aa49";
      case FontAccessLevel.PRIVATE:
        return "#d81b1b";
      default:
        return "#9E9E9E";
    }
  };

  const handleSort = (field: typeof tableSort.field) => {
    setTableSort((prev) => ({
      field,
      order: prev.field === field && prev.order === "asc" ? "desc" : "asc",
    }));
  };

  const handleUpdateFontStatus = async (
    fontKey: string,
    disabled: boolean,
    disabledReason?: string
  ) => {
    setSwitchLoading((prev) => ({ ...prev, [fontKey]: true }));
    try {
      const updateData: IUpdateFontStatusInputModel = {
        key: fontKey,
        disabled,
        disabledReason: disabled
          ? disabledReason || "Fonte indisponível."
          : undefined,
      };
      await updateFontStatus(updateData);
    } catch (error) {
      console.error("Failed to update font status:", error);
    } finally {
      setSwitchLoading((prev) => ({ ...prev, [fontKey]: false }));
    }
  };

  const handleClearMetrics = async () => {
    setClearMetricsLoading(true);
    try {
      await clearFontMetrics();
    } catch (error) {
      console.error("Failed to clear font metrics:", error);
    } finally {
      setClearMetricsLoading(false);
    }
  };

  // Calcula altura dinâmica do gráfico
  const chartHeight = useMemo(() => {
    const baseHeight = 300;
    const heightPerItem = 15;
    return baseHeight + chartData.length * heightPerItem;
  }, [chartData.length]);

  return (
    <>
      <Paper
        sx={{
          p: 4,
          mb: 3,
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        <Typography
          component="p"
          variant="h4"
          color="primary"
          sx={{
            mb: 3,
            fontWeight: 600,
            position: "relative",
            "&:after": {
              content: '""',
              position: "absolute",
              bottom: -8,
              left: 0,
              width: 60,
              height: 3,
              backgroundColor: theme.palette.primary.main,
              borderRadius: 4,
            },
          }}
        >
          Visão geral das fontes
        </Typography>
        {isLoadingFonts ? (
          <CardContainer>
            <div className="cards">
              {Array.from({ length: 4 }).map((_, index) => (
                <Skeleton
                  key={index}
                  sx={{
                    height: 120,
                    minWidth: "200px",
                    maxWidth: "300px",
                    margin: "10px",
                    flex: "1 1 calc(20% - 40px)",
                    borderRadius: "8px",
                  }}
                  animation="wave"
                  variant="rectangular"
                />
              ))}
            </div>
          </CardContainer>
        ) : (
          <CardContainer>
            <div className="cards">
              <KpiCard
                title="Total de Fontes"
                value={stats.totalFonts}
                icon={<FormatListBulletedIcon />}
              />
              <KpiCard
                title="Fontes Ativas"
                value={stats.activeFonts}
                icon={<ChecklistIcon />}
                backgroundColor="#167a12"
              />
              <KpiCard
                title="Fontes Inativas"
                value={stats.inactiveFonts}
                icon={<PlaylistRemoveIcon />}
                backgroundColor="#af1a3a"
              />
              <KpiCard
                title="Taxa Média de Sucesso"
                value={stats.averageSuccessRate}
                percent
                icon={<InsightsIcon />}
                backgroundColor="#107066"
              />
            </div>
          </CardContainer>
        )}
      </Paper>

      <Paper
        sx={{
          p: 4,
          mb: 3,
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        <CardHeader>
          <Typography
            component="p"
            variant="h4"
            color="primary"
            sx={{
              fontWeight: 600,
              position: "relative",
              "&:after": {
                content: '""',
                position: "absolute",
                bottom: -8,
                left: 0,
                width: 60,
                height: 3,
                backgroundColor: theme.palette.primary.main,
                borderRadius: 4,
              },
            }}
          >
            Métricas das Fontes
          </Typography>
          <div
            style={{
              display: "flex",
              gap: "12px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <Select
              value={metric}
              style={{ width: 200 }}
              onChange={(value) => setMetric(value)}
              options={[
                { value: "executions", label: "Número de Execuções" },
                { value: "time", label: "Tempo Médio de Execução" },
                { value: "successRate", label: "Taxa de Sucesso" },
              ]}
            />
            <Button
              icon={
                sortOrder === "desc" ? (
                  <SortAscendingOutlined />
                ) : (
                  <SortDescendingOutlined />
                )
              }
              onClick={() =>
                setSortOrder(sortOrder === "desc" ? "asc" : "desc")
              }
            >
              {sortOrder === "desc" ? "Decrescente" : "Crescente"}
            </Button>
            <Select
              value={usageLimit}
              style={{ width: 120 }}
              onChange={(value) => setUsageLimit(value)}
              options={[
                { value: 5, label: "Top 5" },
                { value: 10, label: "Top 10" },
                { value: 20, label: "Top 20" },
                { value: 50, label: "Top 50" },
                { value: -1, label: "Todas" },
              ]}
            />
            <Popconfirm
              title="Limpar Métricas"
              description="Deseja limpar todas as métricas das fontes? Esta ação não pode ser desfeita."
              okText="Sim"
              cancelText="Não"
              onConfirm={handleClearMetrics}
              okButtonProps={{ disabled: clearMetricsLoading }}
            >
              <Button danger loading={clearMetricsLoading}>
                Limpar Métricas
              </Button>
            </Popconfirm>
          </div>
        </CardHeader>
        {isLoadingDetails || isLoadingFonts ? (
          <Skeleton
            sx={{ height: 400, borderRadius: "8px" }}
            animation="wave"
            variant="rectangular"
          />
        ) : chartData.length > 0 ? (
          <GraphContainer style={{ minHeight: `${chartHeight}px` }}>
            <Bar {...barConfig} height={chartHeight} />
          </GraphContainer>
        ) : (
          <div
            style={{
              height: "200px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "#666",
              fontStyle: "italic",
            }}
          >
            <Typography>Não há dados disponíveis para exibir.</Typography>
          </div>
        )}
      </Paper>

      <Paper
        sx={{
          p: 4,
          mb: 3,
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
        }}
      >
        <CardHeader>
          <Typography
            component="p"
            variant="h4"
            color="primary"
            sx={{
              fontWeight: 600,
              position: "relative",
              "&:after": {
                content: '""',
                position: "absolute",
                bottom: -8,
                left: 0,
                width: 60,
                height: 3,
                backgroundColor: theme.palette.primary.main,
              },
            }}
          >
            Lista de Fontes
          </Typography>
        </CardHeader>
        <TableContainer>
          <Table
            stickyHeader
            sx={{
              mt: 3,
              "& .MuiTableCell-root": { padding: "16px 12px" },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  th: {
                    backgroundColor: "white",
                    "& p": {
                      fontWeight: "bold",
                      color: theme.palette.primary.main,
                    },
                    "& div": {
                      display: "flex",
                      alignItems: "center",
                      "& button": { ml: 1 },
                    },
                  },
                }}
              >
                <TableCell>
                  <Typography>#</Typography>
                </TableCell>
                <TableCell align="left">
                  <div>
                    <Typography>Nome</Typography>
                    <IconButton
                      size="small"
                      style={{ margin: 0, padding: "2px" }}
                      onClick={() => handleSort("name")}
                    >
                      <Sort />
                    </IconButton>
                  </div>
                </TableCell>
                <TableCell align="left">
                  <Typography>Identificador</Typography>
                </TableCell>
                <TableCell align="center">
                  <div>
                    <Typography>Tipo</Typography>
                    <IconButton
                      size="small"
                      style={{ margin: 0, padding: "2px" }}
                      onClick={() => handleSort("type")}
                    >
                      <Sort />
                    </IconButton>
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div>
                    <Typography>Taxa de Sucesso</Typography>
                    <IconButton
                      size="small"
                      style={{ margin: 0, padding: "2px" }}
                      onClick={() => handleSort("successRate")}
                    >
                      <Sort />
                    </IconButton>
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div>
                    <Typography>Execuções</Typography>
                    <IconButton
                      size="small"
                      style={{ margin: 0, padding: "2px" }}
                      onClick={() => handleSort("executions")}
                    >
                      <Sort />
                    </IconButton>
                  </div>
                </TableCell>
                <TableCell align="center">
                  <div>
                    <Typography>Duração Média</Typography>
                    <IconButton
                      size="small"
                      style={{ margin: 0, padding: "2px" }}
                      onClick={() => handleSort("duration")}
                    >
                      <Sort />
                    </IconButton>
                  </div>
                </TableCell>
                <TableCell align="center">
                  <Typography>Acesso</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>Status</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>Ações</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingFonts ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={15} />
                    <Typography
                      variant="caption"
                      color="text"
                      sx={{ fontStyle: "italic" }}
                    >
                      Carregando fontes...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : isLoadingDetails ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress size={15} />
                    <Typography
                      variant="caption"
                      color="text"
                      sx={{ fontStyle: "italic" }}
                    >
                      Carregando detalhes das fontes...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : sortedFonts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Typography
                      variant="caption"
                      color="text"
                      sx={{ fontStyle: "italic" }}
                    >
                      Nenhuma fonte encontrada...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedFonts.map(
                  (font: IAvailableExternalFontViewModel, idx) => {
                    return (
                      <TableRow
                        data-testid="rowFont"
                        key={font.key}
                        sx={{
                          "&:last-child td, &:last-child th": {
                            border: 0,
                          },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <TableCell align="left">
                          <Typography color="primary" sx={{ fontWeight: 500 }}>
                            {(idx + 1).toString().padStart(3, "0")}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">
                          <Typography sx={{ fontWeight: 500 }}>
                            {font.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="left">{font.key}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={SearchTypeText[font.type]}
                            sx={{
                              backgroundColor: getTypeBadgeColor(font.type),
                              color: "white",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            sx={{
                              color:
                                font.successRate && font.successRate > 70
                                  ? "#167a12"
                                  : font.successRate && font.successRate > 40
                                  ? "#e6a700"
                                  : font.successRate != null
                                  ? "#af1a3a"
                                  : "inherit",
                              fontWeight: 500,
                            }}
                          >
                            {font.successRate != null
                              ? `${font.successRate}%`
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {font.executionCount ?? 0}
                        </TableCell>
                        <TableCell align="center">
                          {font.averageExecutionTimeSeconds != null
                            ? formatExecutionTime(
                                font.averageExecutionTimeSeconds
                              )
                            : formatExecutionTime(0)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={FontAccessLevelText[font.accessLevel]}
                            sx={{
                              backgroundColor: getAccessLevelBadgeColor(
                                font.accessLevel
                              ),
                              color: "white",
                              fontWeight: 500,
                              fontSize: "0.75rem",
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {switchLoading[font.key] ? (
                            <CircularProgress size={20} />
                          ) : !font.disabled ? (
                            <Popconfirm
                              title="Desabilitar Fonte"
                              okButtonProps={{
                                disabled: switchLoading[font.key],
                              }}
                              description={
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8,
                                    width: "300px",
                                  }}
                                >
                                  <Typography variant="body2">
                                    Informe a razão para desabilitar a fonte:
                                  </Typography>
                                  <Input
                                    id={"disable-confirm-input-" + font.key}
                                    placeholder="Fonte temporariamente indisponível"
                                  />
                                </div>
                              }
                              onConfirm={() => {
                                const reasonInput = document.querySelector(
                                  `#disable-confirm-input-${font.key}`
                                ) as HTMLInputElement;
                                const reason = reasonInput?.value;
                                handleUpdateFontStatus(font.key, true, reason);
                              }}
                            >
                              <Switch
                                className="custom-switch"
                                style={{ marginLeft: "10px" }}
                                checked={!font.disabled}
                              />
                            </Popconfirm>
                          ) : (
                            <Popconfirm
                              title="Habilitar Fonte"
                              description="Deseja habilitar esta fonte?"
                              okText="Sim"
                              cancelText="Não"
                              onConfirm={() =>
                                handleUpdateFontStatus(font.key, false)
                              }
                            >
                              <Switch
                                className="custom-switch"
                                style={{ marginLeft: "10px" }}
                                checked={!font.disabled}
                              />
                            </Popconfirm>
                          )}
                        </TableCell>
                        <TableCell align="left">
                          <Grid2
                            sx={{
                              display: "flex",
                              justifyContent: "right",
                            }}
                          >
                            <Dropdown
                              menu={{
                                items: getMenuItems(font),
                                onClick: (e) => {
                                  handleMenuAction(e.key, font);
                                },
                              }}
                              trigger={["click"]}
                              placement="bottomRight"
                              arrow
                            >
                              <IconButton
                                sx={{ padding: "2px", margin: 0 }}
                                aria-label="Actions"
                              >
                                <MoreVert />
                              </IconButton>
                            </Dropdown>
                          </Grid2>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {editModalOpen && selectedFont && (
        <EditExternalFontModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedFont(null);
          }}
          font={selectedFont}
        />
      )}
      {modalOpen && (
        <FontTestModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedFont(null);
          }}
          fontKey={selectedFont?.key || ""}
          fontName={selectedFont?.name || ""}
        />
      )}
    </>
  );
};

export default FontManagerTab;
